import { ReactComponent as DashboardIcon } from "images/icons/chart-line.svg"
import { ReactComponent as CampaignIcon } from "images/icons/badge-percent.svg"
import { ReactComponent as DiscountIcon } from "images/icons/tags.svg"
import { ReactComponent as OrderIcon } from "images/icons/cart-shopping.svg"
import { ReactComponent as ProductIcon } from "images/icons/bags-shopping.svg"
import { ReactComponent as CustomerIcon } from "images/icons/users.svg"
import { ReactComponent as StoreIcon } from "images/icons/store.svg"
import { ReactComponent as SettingsIcon } from "images/icons/gear.svg"

import { SidebarConfig } from "./types"
import { PermissionGroups } from "../routes/constants"


export const sidebarConfig: SidebarConfig = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Business overview",
    icon: DashboardIcon,
    routePath: "/dashboard",
    sortOrder: 1,
    children: [
      {
        id: "dashboard-home",
        title: "Home",
        routePath: "/dashboard/home",
        section: "Dashboards"
      },
      {
        id: "dashboard-sales",
        title: "Sales",
        routePath: "/dashboard/sales",
        requiredGroups: PermissionGroups.salesDashboards.read
      },
      {
        id: "dashboard-insights",
        title: "Insights",
        routePath: "/dashboard/insights",
        configKey: "insightsEnabled",
        requiredGroups: PermissionGroups.insightsDashboards.read
      },
      {
        id: "dashboard-operations",
        title: "Operations",
        routePath: "/dashboard/operations",
        configKey: "operationsEnabled",
        requiredGroups: PermissionGroups.operationsDashboards.read
      },
      {
        id: "dashboard-workflows",
        title: "Workflows",
        routePath: "/dashboard/workflows",
        configKey: "workflowsEnabled",
        requiredGroups: PermissionGroups.workflows.read
      }
    ]
  },
  {
    id: "discounts",
    title: "Discounts",
    description: "See and manage discounts",
    icon: DiscountIcon,
    routePath: "/discounts",
    requiredGroups: PermissionGroups.discounts.read,
    sortOrder: 2,
    children: [
      {
        id: "discount-bundle-groups",
        title: "Bundle groups",
        routePath: "/discounts/bundle-groups/",
        section: "Bundles"
      },
      {
        id: "discount-codes",
        title: "Discount codes",
        routePath: "/discounts/discount-codes",
        section: "Discount code"
      },
      {
        id: "discount-code-generation",
        title: "Code generation jobs",
        routePath: "/discounts/code-generation-jobs/"
      },
      {
        id: "discount-rules",
        title: "Discount rules",
        routePath: "/discounts/discount-rules",
        section: "Discount rule"
      },
      {
        id: "discount-create-rule",
        title: "Create discount rule",
        routePath: "/discounts/create-discount-rule",
        requiredGroups: PermissionGroups.discounts.write
      }
    ]
  },
  {
    id: "campaigns",
    title: "Campaigns",
    description: "Create and manage campaigns",
    icon: CampaignIcon,
    routePath: "/campaigns",
    requiredGroups: PermissionGroups.campaigns.read,
    sortOrder: 3,
    configKey: "campaignEnabled",
    children: [
      {
        id: "campaigns-list",
        title: "Show campaigns",
        routePath: "/campaigns/",
        section: "Campaigns"
      },
      {
        id: "campaigns-create",
        title: "Create campaign",
        routePath: "/campaigns/create-campaign",
        requiredGroups: PermissionGroups.campaigns.write
      }
    ]
  },
  {
    id: "orders",
    title: "Orders",
    description: "See and manage orders",
    icon: OrderIcon,
    routePath: "/orders",
    requiredGroups: PermissionGroups.orders.read,
    sortOrder: 4,
    children: [
      {
        id: "orders-list",
        title: "Orders",
        routePath: "/orders/",
        section: "Order"
      },
      {
        id: "orders-create",
        title: "Create order",
        routePath: "/orders/create-order",
        section: "Create order",
        requiredGroups: PermissionGroups.orders.write
      }
    ]
  },
  {
    id: "customer",
    title: "Customer",
    description: "Order and events overview",
    icon: CustomerIcon,
    routePath: "/customer",
    requiredGroups: PermissionGroups.customers.read,
    sortOrder: 5
  },
  {
    id: "products",
    title: "Products",
    description: "Product parents and variants",
    icon: ProductIcon,
    routePath: "/products",
    requiredGroups: PermissionGroups.products.read,
    sortOrder: 5
  },
  {
    id: "stores",
    title: "Stores",
    description: "Manage sales channels",
    icon: StoreIcon,
    routePath: "/stores",
    requiredGroups: PermissionGroups.stores.read,
    sortOrder: 6,
    children: [
      {
        id: "store-groups",
        title: "Store groups",
        routePath: "/stores/store-groups",
        section: "Store group"
      },
      {
        id: "inventories",
        title: "Inventories",
        routePath: "/stores/inventories",
        section: "Inventory",
        requiredGroups: PermissionGroups.inventories.read
      },
      {
        id: "taxes",
        title: "Tax groups",
        routePath: "/stores/taxes",
        section: "Tax groups",
        requiredGroups: PermissionGroups.tax.read
      }
    ]
  },
  {
    id: "settings",
    title: "Settings",
    description: "Configure your business",
    icon: SettingsIcon,
    routePath: "/settings",
    sortOrder: 7,
    requiredGroups: [
      ...PermissionGroups.providers.read,
      ...PermissionGroups.orders.read,
      ...PermissionGroups.providers.read,
      ...PermissionGroups.system.read,
      ...PermissionGroups.workflows.read,
      ...PermissionGroups.checkouts.read
    ],
    children: [
      {
        id: "settings-providers",
        title: "Providers",
        routePath: "/settings/providers",
        section: "Connections",
        requiredGroups: PermissionGroups.providers.read
      },
      {
        id: "settings-external-events",
        title: "External events",
        routePath: "/settings/external-events",
        requiredGroups: PermissionGroups.externalEvents.read
      },
      {
        id: "settings-order-definitions",
        title: "Order definitions",
        routePath: "/settings/order-definitions",
        section: "Order settings",
        requiredGroups: PermissionGroups.orders.read
      },
      {
        id: "settings-custom-states",
        title: "Custom states",
        routePath: "/settings/custom-states",
        requiredGroups: PermissionGroups.orders.read
      },
      {
        id: "settings-workflows",
        title: "Workflows",
        routePath: "/settings/workflows",
        configKey: "workflowsEnabled",
        requiredGroups: PermissionGroups.workflows.read
      },
      {
        id: "settings-checkout-shipping-fallbacks",
        title: "Shipping fallbacks",
        routePath: "/settings/shipping-fallbacks",
        section: "Checkout settings",
        requiredGroups: PermissionGroups.checkouts.read
      },
      {
        id: "settings-checkout-cost",
        title: "Cost",
        routePath: "/settings/cost",
        requiredGroups: PermissionGroups.costs.read
      },
      {
        id: "settings-usage-plan",
        title: "Usage plan",
        routePath: "/settings/usage-plan",
        section: "Brink configurations",
        requiredGroups: PermissionGroups.system.read
      },
      {
        id: "settings-credentials",
        title: "Credentials",
        routePath: "/settings/credentials",
        requiredGroups: PermissionGroups.system.read
      },
      {
        id: "settings-api-clients",
        title: "API clients",
        routePath: "/settings/api-configs",
        requiredGroups: PermissionGroups.apiClients.read
      },
      {
        id: "settings-manage-users",
        title: "Manage users",
        routePath: "/settings/manage-users",
        section: "Users",
        requiredGroups: PermissionGroups.users.read
      }
    ]
  }
]
